<script setup lang="ts">
import { computed, ref } from 'vue'

/**
 * Lightweight collapsible section — a clickable header (whole title row toggles)
 * with a chevron indicator that rotates on expand. Body lives in the default
 * slot. PrimeVue's Panel toggles only via its corner button and doesn't make
 * the title clickable, so this is the project primitive for that UX.
 *
 * `#header` replaces the title text with richer content (the row still
 * toggles); `v-model:open` lets the caller own or persist the state;
 * `elevated` is the floating-overlay chrome (elevated surface + shadow);
 * `expandsUp` flips the chevron for a bottom-anchored panel that grows upward.
 */
const props = withDefaults(
  defineProps<{ title?: string; defaultOpen?: boolean; elevated?: boolean; expandsUp?: boolean; bodyClass?: string }>(),
  { title: '', defaultOpen: false, elevated: false, expandsUp: false, bodyClass: 'p-3' },
)
const model = defineModel<boolean | undefined>('open', { default: undefined })
const local = ref(props.defaultOpen)
const open = computed<boolean>({
  get: () => model.value ?? local.value,
  set: (value) => {
    local.value = value
    model.value = value
  },
})
</script>

<template>
  <div
    class="rounded-md border border-border"
    :class="elevated ? 'bg-surface-elevated shadow-lg' : 'bg-surface'"
  >
    <button
      type="button"
      class="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      :aria-expanded="open"
      @click="open = !open"
    >
      <slot name="header">
        <span class="text-sm font-semibold text-color">{{ title }}</span>
      </slot>
      <i
        class="pi text-xs text-color-secondary transition-transform duration-200"
        :class="[expandsUp ? 'pi-chevron-up' : 'pi-chevron-down', { 'rotate-180': open }]"
        aria-hidden="true"
      />
    </button>
    <div v-show="open" class="border-t border-border" :class="bodyClass">
      <slot />
    </div>
  </div>
</template>
